import React, { Fragment, useState } from 'react';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { docGeniusPalette } from 'styles/partials/colors';
import { AzulSkeleton } from '@the-ksquare-group/azul-ds';
import { CardListPreview } from 'components/CardListPreview';
import {
  DescriptionRounded,
  KeyboardArrowDown,
  ViewList,
  GridViewRounded,
  Delete,
  CollectionsBookmarkRounded,
} from '@mui/icons-material';
import RadioTag, { Tag } from 'components/RadioTag/RadioTag';
import { QueryBuilder } from 'utils/formatQueryParams';
import EmptyTable from 'components/DataTable/components/EmptyTable/EmptyTable';

import { useNavigate } from 'react-router-dom';
import styles from './TemplatesPage.module.css';
import MenuButton from 'components/MenuButton/MenuButton';
import Button from 'components/Button/Button';
import {
  useGetTemplatesQuery,
  usePatchTemplateMutation,
  usePatchSetDefaultMutation,
  useCreateFileFromTemplateMutation,
} from 'features/template/templateApi';
import TemplateTable from './components/TemplatesTable/TemplatesTable';
import { SearchBar } from 'components/SearchBar';
import { MenuOption } from 'dataTypes/document';
import { ReactComponent as ContractIcon } from 'assets/images/NewFileOptionsIcons/contract-option-icon.svg';
import { ReactComponent as QuoteIcon } from 'assets/images/NewFileOptionsIcons/quote-option-icon.svg';
import { ReactComponent as DocumentIcon } from 'assets/images/NewFileOptionsIcons/document-option-icon.svg';
import { ReactComponent as DefaultContractIcon } from 'assets/images/default-contract-icon.svg';
import { DocumentType } from 'features/document/documentSlice';
import ModalFactory, { ModalType } from 'components/CustomModal/ModalFactory';
import { useAppSelector } from 'app/hooks';
import { selectSupportedDocTypes } from 'features/organization/organizationSlice';
import { selectCurrentUser } from 'features/auth/authSlice';
import { UserRoles } from 'features/auth/authTypes';
import { appAccessRoles } from 'App';
import { selectIsSalesforceAuth } from 'features/salesforce/salesforceSlice';

export function TemplatesPage() {
  const navigate = useNavigate();
  const [searchBy, setSearchBy] = useState('');
  const [isList, setIsList] = useState(false);
  const builder = new QueryBuilder();
  const [patchTemplate] = usePatchTemplateMutation();
  const [patchSetDefault] = usePatchSetDefaultMutation();
  const [createFileFromTemplate] = useCreateFileFromTemplateMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('delete');
  const [modalData, setModalData] = useState({});
  const supportedDocTypes = useAppSelector(selectSupportedDocTypes);
  const isSalesforceAuth = useAppSelector(selectIsSalesforceAuth);
  const user = useAppSelector(selectCurrentUser);
  const showCreateFileFromTemplateOption = appAccessRoles.includes(user?.role as UserRoles);

  const getTagOptions = () => {
    const defaultTagOptions = [
      {
        value: 'all',
        label: 'All Templates',
      },
      {
        value: 'recentlyOpened',
        label: 'Recently Opened',
      },
      {
        value: 'favorites',
        label: 'Favorites',
      },
      {
        value: 'default',
        label: 'Default',
      },
    ];

    const variableTagOptions = [
      {
        value: 'document',
        label: 'Documents',
      },
      {
        value: 'contract',
        label: 'Contracts',
      },
      {
        value: 'quote',
        label: 'Quotes',
      },
      {
        value: 'preclosing',
        label: 'Preclosing',
      },
    ];

    if (isSalesforceAuth && supportedDocTypes) {
      const supportedDocTypesLowerCase = supportedDocTypes.map(type => type.toLowerCase());
      const filteredTagOptions = variableTagOptions.filter(item =>
        supportedDocTypesLowerCase.includes(item.value.toLowerCase())
      );

      return [...defaultTagOptions, ...filteredTagOptions];
    }
    return defaultTagOptions;
  };

  const [selectedTag, setSelectedTag] = useState<Tag>(getTagOptions()[0]);

  const queryString = builder
    .setQueryInclude({ field: 'files' })
    .setQueryWhere({ field: 'name', operator: '$substring', value: searchBy })
    .setQueryWhere({
      field: 'isFavorite',
      operator: '$eq',
      value: selectedTag.value === 'favorites' ? 'true' : undefined,
    })
    .setQueryWhere({
      field: 'isDefault',
      operator: '$eq',
      value: selectedTag.value === 'default' ? 'true' : undefined,
    })
    .setQueryOrder({
      field: 'updatedAt',
      order: selectedTag.value === 'recentlyOpened' ? 'ASC' : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'document' ? DocumentType.DOCUMENT : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'quote' ? DocumentType.QUOTE : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'contract' ? DocumentType.CONTRACT : undefined,
    })
    .setQueryWhere({
      field: 'type',
      operator: '$eq',
      value: selectedTag.value === 'preclosing' ? DocumentType.PRECLOSING : undefined,
    })
    .setQueryInclude({ field: 'createdByUser' })
    .build();

  const { data: templatesData, isFetching } = useGetTemplatesQuery(queryString);
  const templates = templatesData?.data;

  const toggleFavorite = async (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number,
    name: string,
    isDocFavorite: boolean
  ) => {
    event.stopPropagation();
    event.currentTarget.blur();
    try {
      await patchTemplate({ id, name, isFavorite: !isDocFavorite }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOnSetDefault = async (id: number, type: string) => {
    await patchSetDefault({ id, type: type }).unwrap();
  };

  const openModal = (data: { id: number; name: string }) => {
    setModalData({ ...data, isTemplate: true });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const modalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const modalHandler = () => {
    switch (modalType) {
      case 'delete':
        return <ModalFactory type='delete' props={modalProps} />;

      case 'rename':
        return <ModalFactory type='rename' props={modalProps} />;

      case 'duplicate':
        return <ModalFactory type='duplicate' props={modalProps} />;

      default:
        return null;
    }
  };

  const getNewTemplateMenuItems = () => {
    const menuItems = [
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1}>
            <DocumentIcon /> Document
          </Grid>
        ),
        label: 'Document',
        handler: () => {
          navigate('/templates/create/document');
        },
      },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1}>
            <DocumentIcon /> Preclosing
          </Grid>
        ),
        label: 'Preclosing',
        handler: () => {
          navigate('/templates/create/preclosing');
        },
      },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1}>
            <ContractIcon /> Contract
          </Grid>
        ),
        label: 'Contract',
        handler: () => {
          navigate('/templates/create/contract');
        },
      },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1}>
            <QuoteIcon /> Quote
          </Grid>
        ),
        label: 'Quote',
        handler: () => {
          navigate('/templates/create/quote');
        },
      },
    ];

    const supportedDocTypesLowerCase = supportedDocTypes.map(type => type.toLowerCase());

    const filteredMenuItems = menuItems.filter(item => {
      const labelLowerCase = item.label.toLowerCase();
      return supportedDocTypesLowerCase.includes(labelLowerCase);
    });

    return filteredMenuItems;
  };

  const handleOnCreateFileFromTemplate = async (id: number) => {
    const {
      data: { type, id: createdDocumentId },
    } = await createFileFromTemplate(id).unwrap();
    if (createdDocumentId) {
      navigate(`/documents/edit/${type.toLowerCase()}/${createdDocumentId}`);
    }
  };

  const templateCardMenuItems = (
    id: number,
    name: string,
    status: string,
    type: string,
    isDefault?: boolean
  ): MenuOption[] => {
    const createFileFromTemplateOption = {
      item: (
        <Grid container justifyContent='flex-start' gap={1} width='264px'>
          <CollectionsBookmarkRounded color='primary' /> Create file from template
        </Grid>
      ),
      handler: () => {
        handleOnCreateFileFromTemplate(id);
      },
    };

    const commonMenuItems = [
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <DescriptionRounded color='primary' /> Rename
          </Grid>
        ),
        handler: () => {
          handleOnRename(id, name);
        },
      },
      // TODO: enable duplicate template option when endpoint is created
      // {
      //   item: (
      //     <Grid container justifyContent='flex-start' gap={1} width='264px'>
      //       <FileCopy sx={{ color: docGeniusPalette.primaryBlue }} /> Duplicate
      //     </Grid>
      //   ),
      //   handler: () => {
      //     setModalType!('duplicate');
      //     openModal({ id, name });

      //   },
      // },
      {
        item: (
          <Grid container justifyContent='flex-start' gap={1} width='264px'>
            <Delete color='primary' /> Delete
          </Grid>
        ),
        handler: () => {
          handleOnDelete(id, name);
        },
      },
    ];

    const menuItemsWithDefault: MenuOption[] = [
      {
        item: (
          <Grid container alignItems={'center'} justifyContent='flex-start' gap={1} width='264px'>
            <DefaultContractIcon />
            {`Set as default ${type.toLowerCase()}`}
          </Grid>
        ),
        handler: () => {
          handleOnSetDefault(id, type);
        },
      },
      ...commonMenuItems,
    ];

    const menuItems = isDefault ? commonMenuItems : menuItemsWithDefault;

    return showCreateFileFromTemplateOption ? [createFileFromTemplateOption, ...menuItems] : menuItems;
  };

  const handleOnRename = (id: number, name: string) => {
    setModalType!('rename');
    openModal({ id, name });
  };

  const handleOnDelete = (id: number, name: string) => {
    setModalType!('delete');
    openModal({ id, name });
  };

  const handleOnChangeTag = (tag: Tag) => {
    setSelectedTag(tag);
  };

  const handleOnSearch = (query: string) => {
    setSearchBy(query);
  };

  const handleOnClickNewTemplate = () => {
    navigate('/templates/create/document');
  };

  function handleOpenTemplate(id: number, type: string) {
    if (type) {
      navigate(`/templates/edit/${type.toLowerCase()}/${id}`);
    } else {
      navigate(`/templates/edit/document/${id}`);
    }
  }

  const renderTemplates = () => {
    if (isFetching) {
      return (
        <Fragment>
          {!isList ? (
            <Box display='flex' alignItems={'center'} gap={'24px'}>
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
              <AzulSkeleton height={334} width={264} variant='rounded' />
            </Box>
          ) : (
            <AzulSkeleton height={219} variant='rounded' />
          )}
        </Fragment>
      );
    }

    if (!templates?.length) {
      return (
        <EmptyTable
          title='There is nothing here!'
          subtitle='Start creating new templates to get started'
          buttonText='New Template'
          onClick={handleOnClickNewTemplate}
        />
      );
    }
    return !isList ? (
      <CardListPreview
        data={templates}
        menuItems={templateCardMenuItems}
        handleOpen={handleOpenTemplate}
        toggleFavorite={toggleFavorite}
      />
    ) : (
      //TODO: create a custom component for both document/template table
      <TemplateTable
        data={templates}
        onDelete={handleOnDelete}
        onRename={handleOnRename}
        onSetDefault={handleOnSetDefault}
        onCreateFileFromTemplate={handleOnCreateFileFromTemplate}
      />
    );
  };

  return (
    <Fragment>
      {isModalOpen && modalHandler()}
      <Grid container xs={12} justifyContent='center'>
        <Grid container className={styles.container} xs={12} md={8} sm={12}>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Typography
                component='h1'
                gutterBottom
                color={docGeniusPalette.primaryBlue.toString()}
                align='left'
                fontSize={'24px'}
                fontWeight={500}
                marginBottom={0}
              >
                My Templates
              </Typography>
            </Grid>
            <Grid item>
              <MenuButton options={getNewTemplateMenuItems()}>
                <Button variant='primary' className={styles.button}>
                  New Template
                  <KeyboardArrowDown />
                </Button>
              </MenuButton>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <SearchBar placeholder='Search file' value={searchBy} onSearch={handleOnSearch} />
          </Grid>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <RadioTag selected={selectedTag} handleChange={handleOnChangeTag} options={getTagOptions()} />
            </Grid>
            <Grid item>
              <Tooltip title='Grid View' arrow>
                <IconButton onClick={() => setIsList(false)} sx={{ color: isList ? '#B3C2DA' : '#265183' }}>
                  <GridViewRounded />
                </IconButton>
              </Tooltip>
              <Tooltip title='List View' arrow>
                <IconButton onClick={() => setIsList(true)} sx={{ color: !isList ? '#B3C2DA' : '#265183' }}>
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          {renderTemplates()}
        </Grid>
      </Grid>
    </Fragment>
  );
}
